// Adapters from raw eDOS TestSpecification rows → the shapes consumed
// by the lab-portal eDOS pages (EdosTests, EdosDepartments,
// EdosSpeciality, EdosTestDetail). The public website's
// `mapApiToTestDetail` handles a thinner shape; this adapter expands on
// it with branch availability, ICD-10 fallbacks, stability/retention,
// rejection-criteria splitting, and the L2L vs MRP partner economics
// the lab portal surfaces.
//
// Source-of-truth field names come from the Laravel response observed
// against /get-edos/item/{id} and /get-edos/testsList:
//   id, test_code, test_name, parameters (CSV), gender, age_range,
//   fasting, home_collection ('Y'/null), walk_in ('Y'/null),
//   clinical_history_required, consent_form_required, sample_type,
//   sample_volume, sample_container, ship_temperature, store_temperature,
//   sample_retention (days), test_schedule (CSV weekday abbrev),
//   sample_receipt_cutoff (HH:MM:SS), report_tat ('24 Hours'),
//   sample_report (URL|null), l2l_price (string), mrp (string),
//   specialisation, clinical_aid (long-form indication text),
//   special_instructions, departments (CSV), test_methodology,
//   doctor_specialty (CSV), diseases (CSV), location (CSV branch list),
//   nabl ('Y'/'N'), ihos ('IH'/'OH'/null), Pkg/Profile,
//   sample_rejection_creteria (CSV), is_active.

import { BRANCHES } from '../pages/portals/lab/edosData';

// ───────── primitive helpers ─────────

const truthy = (v) =>
  v === true || v === 1 || v === '1' ||
  v === 'Y' || v === 'y' || v === 'Yes' || v === 'yes';

const splitCsv = (s) =>
  s ? String(s).split(',').map((x) => x.trim()).filter(Boolean) : [];

// Split on either commas OR ampersands — sample_type and sample_container
// use ' & ' to delimit multiple containers (e.g. "EDTA Tube & Plain Tube").
const splitCsvOrAmp = (s) =>
  s ? String(s).split(/[,&]/).map((x) => x.trim()).filter(Boolean) : [];

// "M, Tu, W, Th, F, S " → "Mon, Tue, Wed, Thu, Fri, Sat"
const WEEKDAY_MAP = { M: 'Mon', Tu: 'Tue', W: 'Wed', Th: 'Thu', F: 'Fri', S: 'Sat', Su: 'Sun' };
const expandSchedule = (s) => {
  const parts = splitCsv(s);
  if (parts.length === 0) return '—';
  return parts.map((p) => WEEKDAY_MAP[p] || p).join(' · ');
};

// "21:00:00" → "9:00 PM"
const formatCutoff = (s) => {
  if (!s) return '—';
  const [hh, mm] = String(s).split(':');
  const h = Number(hh);
  if (Number.isNaN(h)) return s;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${mm || '00'} ${period}`;
};

// "12 to 14 Hours" → bool fasting + display string
const isFastingRequired = (s) =>
  !!s && s.toLowerCase() !== 'none' && s.toLowerCase() !== 'no' && s.toLowerCase() !== 'not required';

// "M/F" → 'both'  |  "M" → 'male'  |  "F" → 'female'
const normalizeGender = (g) => {
  if (!g) return 'both';
  const v = String(g).toUpperCase();
  if (v === 'M/F' || v === 'BOTH') return 'both';
  if (v === 'M' || v === 'MALE') return 'male';
  if (v === 'F' || v === 'FEMALE') return 'female';
  return 'both';
};

// "3" → "3 days"  (sample_retention is days)
const formatRetention = (s) => {
  if (!s) return '—';
  const n = Number(s);
  if (Number.isNaN(n)) return String(s);
  return `${n} day${n === 1 ? '' : 's'}`;
};

// Try to derive a primary department from the CSV departments field.
// "Biochemistry, Endocrinology" → "Biochemistry"
const primary = (csv) => {
  const list = splitCsv(csv);
  return list[0] || '';
};

// ICD-10 fallback by department — supplies a sensible code when the
// backend doesn't carry an icd10 column. Pulls one or two codes that
// fit the discipline; UI renders these as chips.
const ICD_BY_DEPT = {
  Hematology:                [{ c: 'D64.9', l: 'Anaemia, unspecified' }, { c: 'D72.9', l: 'Disorder of WBCs' }],
  Biochemistry:              [{ c: 'E78.5', l: 'Dyslipidaemia, unspecified' }, { c: 'E11.9', l: 'Type 2 diabetes' }],
  Endocrinology:             [{ c: 'E03.9', l: 'Hypothyroidism, unspecified' }, { c: 'E05.9', l: 'Thyrotoxicosis' }],
  'Immunology / Serology':   [{ c: 'M35.9', l: 'Systemic involvement of connective tissue' }],
  Microbiology:              [{ c: 'A49.9', l: 'Bacterial infection, unspecified' }],
  Coagulation:               [{ c: 'D68.9', l: 'Coagulation defect, unspecified' }],
  'Clinical Pathology':      [{ c: 'N39.0', l: 'Urinary tract infection' }],
  Cardiology:                [{ c: 'I25.10', l: 'Atherosclerotic heart disease' }],
  Histopathology:            [{ c: 'C80.1', l: 'Malignant neoplasm, unspecified' }],
  Cytology:                  [{ c: 'R87.610', l: 'Atypical squamous cells (ASC-US)' }],
  Genetics:                  [{ c: 'Q99.9', l: 'Chromosomal abnormality, unspecified' }],
  Package:                   [{ c: 'Z00.00', l: 'General health screening' }],
  Profile:                   [{ c: 'Z00.00', l: 'General health screening' }],
};

// ───────── branch availability derivation ─────────

// Laravel's `location` column is a CSV of branch *names* (e.g.
// "Anantapur, Begumpet"). Match those against our BRANCHES table by
// either exact branch name or city. Any branch not present in the CSV
// is shown as "referral → NRL" in the lab portal's Branch Availability
// table; CCs that don't carry the test fall back to 'not-offered'.
const matchesBranch = (branchEntry, locationCsv) => {
  const list = splitCsv(locationCsv).map((s) => s.toLowerCase());
  const name = branchEntry.branch.toLowerCase();
  const city = branchEntry.city.toLowerCase();
  // "Begumpet (NRL)" should match "Begumpet"
  const stripped = name.replace(/\s*\(.+?\)\s*/g, '').trim();
  return list.some((loc) => loc === name || loc === city || loc === stripped);
};

const deriveBranchAvailability = (api, tat) => {
  const csv = api.location;
  return BRANCHES.map((b) => {
    const inHouse = matchesBranch(b, csv);
    if (inHouse) {
      return {
        branch: b.branch, city: b.city, type: b.type,
        status: 'in-house',
        localTat: tat || api.report_tat || '—',
      };
    }
    // Cardiology procedures (ECG etc.) are in-clinic at every reference
    // lab even if the location CSV doesn't list them — kept simple by
    // honouring the API list as the source of truth, so we just mark
    // the rest as 'referral'.
    return {
      branch: b.branch, city: b.city, type: b.type,
      status: 'referral',
      localTat: `${tat || api.report_tat || '—'} + transit`,
    };
  });
};

// ───────── public adapters ─────────

// Compact card row — used by EdosTests / EdosDepartments / EdosSpeciality
// list grids. Keep the shape close to the static `TESTS` array in
// edosData.js so EdosResultsList doesn't need a parallel renderer
// (same fields: code, name, aliases[], dept, tat, schedule, cutoff,
// mrp, l2l, specs[], params[]).
export const mapApiToTestRow = (api) => {
  if (!api) return null;
  const params = splitCsv(api.parameters);
  return {
    id:        api.id,
    code:      api.test_code || `EDOS-${api.id}`,
    name:      api.test_name || '—',
    // `complete_test_name` is the long-form alias string (CSV). Split
    // into an array so EdosResultsList can render "aka X · Y · Z".
    aliases:   splitCsv(api.complete_test_name),
    dept:      primary(api.departments) || 'General',
    specs:     splitCsv(api.doctor_specialty),
    diseases:  splitCsv(api.diseases),
    tat:       api.report_tat || '—',
    schedule:  expandSchedule(api.test_schedule),
    cutoff:    formatCutoff(api.sample_receipt_cutoff),
    mrp:       Number(api.mrp) || 0,
    l2l:       Number(api.l2l_price) || 0,
    paramCount: params.length,
    params,
    nabl:      truthy(api.nabl),
    isPackage: api['Pkg/Profile'] === 'Package' || (api.test_code || '').startsWith('TLPK'),
    isProfile: api['Pkg/Profile'] === 'Profile' || (api.test_code || '').startsWith('TLPR'),
    homeCollection: truthy(api.home_collection),
    walkIn:    truthy(api.walk_in),
  };
};

// Full detail — hydrates every section the lab-portal EdosTestDetail
// page renders. Falls back to per-department defaults when a field is
// null/empty so the UI never shows blank rows. Mirrors the fallback
// behaviour of the static getTestDetail() in edosData.js.
export const mapApiToEdosDetail = (api) => {
  if (!api) return null;
  const dept = primary(api.departments) || 'General';
  const tat = api.report_tat || '—';
  const params = splitCsv(api.parameters);
  const mrp = Number(api.mrp) || 0;
  const l2l = Number(api.l2l_price) || 0;

  return {
    // ── identifiers / hero ──
    id:           api.id,
    code:         api.test_code || `EDOS-${api.id}`,
    name:         api.test_name || '—',
    aliases:      api.complete_test_name || '',
    dept,
    specs:        splitCsv(api.doctor_specialty),
    diseases:     splitCsv(api.diseases),
    tag:          api.specialisation || api.test_type || null,
    nabl:         truthy(api.nabl),
    inHouse:      api.ihos === 'IH',
    isPackage:    api['Pkg/Profile'] === 'Package' || (api.test_code || '').startsWith('TLPK'),
    isProfile:    api['Pkg/Profile'] === 'Profile' || (api.test_code || '').startsWith('TLPR'),

    // ── pricing ──
    mrp,
    l2l,
    margin:       mrp - l2l,
    marginPct:    mrp > 0 ? Math.round(((mrp - l2l) / mrp) * 100) : 0,

    // ── applicability ──
    testType:        api.test_type || (api['Pkg/Profile'] === 'Package' ? 'Package' : 'Routine'),
    methodology:     api.test_methodology || '—',
    disease:         primary(api.diseases) || 'General Health Screening',
    gender:          normalizeGender(api.gender),
    ageRange:        api.age_range || 'All ages',
    homeCollection:  truthy(api.home_collection),
    walkIn:          truthy(api.walk_in),
    fastingRequired: isFastingRequired(api.fasting),
    fastingDuration: api.fasting && isFastingRequired(api.fasting) ? api.fasting : null,
    clinicalHistory: truthy(api.clinical_history_required),
    consentForm:     truthy(api.consent_form_required),
    accreditation:   truthy(api.nabl) ? 'NABL IN-SCOPE' : 'NABL OUT-OF-SCOPE',

    // ── sample specs ──
    sampleType:      api.sample_type || '—',
    sampleTypes:     splitCsvOrAmp(api.sample_type),
    sampleContainer: api.sample_container || '—',
    sampleContainers: splitCsvOrAmp(api.sample_container),
    sampleVolume:    api.sample_volume || '—',
    shipTemp:        api.ship_temperature || '2–8 °C',
    storeTemp:       api.store_temperature || '2–8 °C',
    temperature:     api.temperature || null, // 'Refrigerated' / 'Ambient' / 'Frozen'
    stability:       api.refrigerated || api.store_temperature || '—',
    sampleRetention: formatRetention(api.sample_retention),
    preAnalytical:   api.special_instructions || '—',

    // ── schedule / TAT ──
    tat,
    schedule:        expandSchedule(api.test_schedule),
    scheduleRaw:     api.test_schedule || '',
    cutoff:          formatCutoff(api.sample_receipt_cutoff),
    batchTiming:     api.batch_timing || null,

    // ── report ──
    reportFormat:    'Numeric with reference range',
    sampleReport:    api.sample_report || null,
    rejectionCriteria: splitCsv(api.sample_rejection_creteria),

    // ── analytic content ──
    parameters:           params,
    paramCount:           params.length,
    clinicalSignificance: api.clinical_aid || '',
    specialInstructions:  api.special_instructions || '',

    // ── ICD-10 fallback (UI shows chips) ──
    icd10: ICD_BY_DEPT[dept] || [{ c: 'Z00.00', l: 'General health screening' }],

    // ── branch availability ──
    branchAvailability: deriveBranchAvailability(api, tat),

    // raw passthrough so callers can read anything we missed
    _raw: api,
  };
};

// Group an array of `mapApiToTestRow` outputs by department. Returns
// [{ dept, count, tests }] sorted by count desc — the shape EdosDepartments
// renders directly.
export const groupByDepartment = (rows = []) => {
  const map = new Map();
  for (const r of rows) {
    const key = r.dept || 'General';
    if (!map.has(key)) map.set(key, { dept: key, count: 0, tests: [] });
    const g = map.get(key);
    g.count += 1;
    g.tests.push(r);
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
};

// Group rows by doctor specialty (each row may have multiple specs;
// it appears once per specialty). Same shape as groupByDepartment.
export const groupBySpecialty = (rows = []) => {
  const map = new Map();
  for (const r of rows) {
    const specs = (r.specs && r.specs.length > 0) ? r.specs : ['General'];
    for (const s of specs) {
      if (!map.has(s)) map.set(s, { spec: s, count: 0, tests: [] });
      const g = map.get(s);
      g.count += 1;
      g.tests.push(r);
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
};
